import { Fragment, useEffect, useState } from 'react';

import { Control, useController, useWatch } from 'react-hook-form'
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { Box, Stack, TextField, TextFieldProps } from '@mui/material';
import { ISelectOptions } from './SelectInput';

export interface IAutoCompleteInputProps extends Pick<TextFieldProps, "size"> {
    id: string;
    name: string;
    control: Control<any, object>;
    options: ISelectOptions[];
    placeholder?: string;
    label?: string;
    multiple?: boolean;
    disabled?: boolean;
    sx?: any;
    variant?: 'outlined' | 'standard' | 'filled' | undefined;
    onChange?: (...rest: any) => any;
    setValueKey?: string;
}

export default function AutoCompleteInput({ control, id, size = "small", setValueKey, name, options, onChange, label, variant = 'outlined', sx, multiple = false, placeholder, disabled, ...other }: IAutoCompleteInputProps) {
    const { field, fieldState } = useController({ control, name })

    const [display,setDisplay] = useState<{text:string,figure:any[]}>({
        text:!multiple && field.value?.option || options?.find( o => o.value === field.value)?.option || options?.[0]?.option || "",
        figure:!multiple &&  field.value?.figure || options?.find( o => o.value === field.value)?.figure || options?.[0]?.figure || []
    })

    const displayAction = {
        restore: () => {
            if (!display.text) {
                setDisplay({
                    text:!multiple && field.value?.option || options?.find( o => o.value === field.value)?.option || options?.[0].option || "",
                    figure:!multiple &&  field.value?.figure || options?.find( o => o.value === field.value)?.figure || options?.[0]?.figure || []
                })
            }
        },
        reset:()=>{
            setDisplay({
                text:"",
                figure:[]
            })
        }
    }

    const handleBlur = () => {
        field.onBlur()
        !multiple && displayAction.restore()
    }



    const handleKeyDown = (e) => {
        if (multiple) return;
        if (e.code === "Enter") {
            displayAction.restore()
        }
        if (!["ArrowDown", "ArrowUp"].includes(e.code)) {
            if (display.text) {
                displayAction.reset()
            }
        }
    }

    const handleAutoCompleteChange = (event, newValue) => {
        let value = multiple ? newValue : field.value;

        if(newValue.length){
            value = multiple ? newValue : setValueKey ? newValue[1]?.[setValueKey] : newValue[1]
            if(!multiple){
                setDisplay({
                    text:newValue[1].option || "",
                    figure:newValue[1].figure || []
                })
            }
        }
        if(value){
            onChange?.(event, value);
            field.onChange(value);
        }
    }

    return (
        <Stack position={"relative"} >
            <Autocomplete
                disableClearable={!multiple}
                multiple
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                id={id}
                value={field.value ? multiple ? field.value : [field.value] : [options[0]]}
                onChange={handleAutoCompleteChange}
                disabled={disabled}
                options={options}
                isOptionEqualToValue={(option, value) => option?.value === value?.value || option?.value === value}
                getOptionLabel={(option) => {
                    return option.option || options.find(i => i.value === option)?.option || ""
                }}
                getOptionDisabled={(option) => multiple ? field.value?.some(fieldOption => fieldOption.option === option.option) : option.value === field.value }
                renderOption={(props, option) => (
                    <Box key={option?.option} sx={{ position: "relative", padding: 1, cursor: "pointer", ...props?.style }} {...props as unknown as any}>
                        {
                            option.element ? option.element : <Box display={'inline-block'}>{option.option}</Box>
                        }
                        {
                            option?.figure?.length && (
                                <Stack position={'absolute'} right={'0'} top={'50%'} sx={{ transform: 'translateY(-50%)' }} display={'inline-flex'} direction={'row'}>
                                    {
                                        option.figure?.map((figure, index) => <Fragment key={index}>{figure}</Fragment>)
                                    }
                                </Stack>
                            )
                        }
                    </Box>
                )}
                renderTags={(values, getTagProps) =>
                    values.map((option, index) => {
                        if (multiple) {
                            return (
                                <Chip
                                    variant="outlined"
                                    label={option.option}
                                    size="small"
                                    {...getTagProps({ index })}
                                />
                            )
                        }else{
                            return <Stack direction={"row"} paddingLeft="0.5rem" {...getTagProps({index})}>
                                {
                                    !!display?.text && <Box className='item' display={'inline-block'}>{display.text}</Box>
                                }
                                {
                                    !!display?.figure?.length && (
                                        <Stack position={'absolute'} right={'2.6rem'} top={'50%'} sx={{ transform: 'translateY(-50%)' }} display={'inline-flex'} direction={'row'}>
                                            {
                                                display.figure?.map((figure, index) => <Fragment key={index}>{figure}</Fragment>)
                                            }
                                        </Stack>
                                    )
                                }
                            </Stack>
                        }
                    })
                }
                renderInput={(params) => <TextField label={label} variant={variant} placeholder={multiple ? placeholder : display.text ? '' : placeholder} {...params} error={!!fieldState?.error} helperText={fieldState?.error?.message} inputProps={{autoComplete: `new-${name}`, ...params.inputProps }} size={size} />}
                sx={sx}
            />
        </Stack>
    );
}
